import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarPlus, ArrowRight } from "lucide-react";
import api from "@/lib/api";

// Beta feedback (Feb 2026): athletes forgetting to build next week's plan
// until Monday morning when it's already the current week. This Home-hub
// banner shows Sat + Sun ONLY when the athlete's most-recent plan's week
// hasn't been advanced past the current one. Dismissible per-weekend via
// sessionStorage so it doesn't nag once consciously skipped.
const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (d) => { const nd = new Date(d); const dow = (nd.getDay() + 6) % 7; nd.setDate(nd.getDate() - dow); return nd; };

export default function BuildNextWeekNudge() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [nextMonday, setNextMonday] = useState("");

  useEffect(() => {
    const now = new Date();
    const dow = now.getDay(); // 0=Sun, 6=Sat
    if (dow !== 0 && dow !== 6) return; // weekend only
    // Compute next Monday's ISO.
    const nm = new Date(now);
    const add = dow === 0 ? 1 : 2;
    nm.setDate(nm.getDate() + add);
    const nmIso = toISO(nm);
    setNextMonday(nmIso);
    if (sessionStorage.getItem(`spc_build_next_week_dismissed_${nmIso}`)) return;
    let cancelled = false;
    api.get("/plans").then(({ data }) => {
      if (cancelled) return;
      const plans = data || [];
      // Only nudge if no saved plan already covers next week — otherwise
      // the athlete has clearly already built it.
      const hasNext = plans.some((p) => p.weekStart === nmIso);
      if (!hasNext) setShow(true);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  if (!show) return null;
  const dismiss = () => { sessionStorage.setItem(`spc_build_next_week_dismissed_${nextMonday}`, "1"); setShow(false); };

  return (
    <div data-testid="build-next-week-nudge" className="mt-8 border border-[#E2E30A]/50 bg-[#E2E30A]/[0.06] p-4 sm:p-5 flex items-start gap-3 sm:gap-4">
      <span className="inline-flex items-center justify-center h-10 w-10 shrink-0 border border-[#E2E30A]/40 bg-[#E2E30A]/10">
        <CalendarPlus className="h-4 w-4 text-[#E2E30A]" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#E2E30A]">Next week's fuelling</p>
        <p className="text-sm text-zinc-100 mt-1">
          Sunday's the day — spend two minutes now building your plan for
          w/c <span className="font-mono text-[#E2E30A]">{nextMonday}</span> so you start Monday fuelled and focused.
        </p>
        <div className="flex gap-2 mt-3">
          <button data-testid="build-next-week-cta" type="button" onClick={() => navigate("/app?intent=new")}
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-[#E2E30A] hover:bg-[#c8c908] text-black px-3 py-1.5 transition-colors">
            Build next week <ArrowRight className="h-3 w-3" />
          </button>
          <button data-testid="build-next-week-dismiss" onClick={dismiss}
            className="inline-flex items-center text-[11px] uppercase tracking-wider text-zinc-400 hover:text-white px-3 py-1.5 border border-white/10 hover:border-white/30 transition-colors">
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
